import { useQuery } from "@msuite/ui/query";
import { responseHandler } from "@msuite/ui/utils";
import { guardString } from "@msuite/utils/strings";
import type { Enums } from "@msuite/utils/types";
import { client } from "@/hono";
import { useAuth } from "@/hooks/use-auth";
import { useCitadel } from "@/hooks/use-citadel";

export function usePartnerships(
	as: "host" | "partner" | "both" = "partner",
	state?: Array<Enums<"state">>,
) {
	/** Hooks */
	const { organization } = useAuth();
	const { citadel } = useCitadel();

	/** Query */
	const query = useQuery({
		queryKey: ["organizationPartnerships", as, guardString(organization.id), guardString(state)],
		queryFn: async () => {
			if (!organization) return [];
			const response = await client.organizations[":orgId"].partnerships.$get({
				param: { orgId: guardString(organization.id) },
				query: {
					state,
					as,
				},
			});
			const data = await responseHandler(response);
			return data.partnerships;
		},
		staleTime: 1_000 * 60 * 5,
		// `GET /organizations/:orgId/partnerships` gates `read Partner`, which the base role holds no
		// rule for. The gate travels with the hook rather than with each caller, because two of the
		// three read it outside the partner screens — the ticket form's assignee picker and the
		// board's add-crew popover — and a member editing a ticket would otherwise collect a refusal
		// toast per mount. Without the rule the picker simply offers colleagues and no partner, which
		// is the assignment the server would accept.
		enabled: citadel?.can("read", "Partner") === true,
	});

	return {
		...query,
		partnerships: query.data,
	};
}
